import React, { useState, useEffect } from 'react';
import HistoricalCostDashboard from './HistoricalCostDashboard';
import CostExplanationPanel from './CostExplanationPanel';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Table, 
  Tag, 
  Space, 
  Button, 
  Spin,
  Alert,
  Typography,
  Divider,
  Tooltip,
  Switch
} from 'antd';
import { 
  DashboardOutlined, 
  ReloadOutlined, 
  CloudServerOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  PieChartOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { monitoringApi } from '../services/monitoring';

const { Title, Text } = Typography;

const MonitoringDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    pendingJobs: {},
    activeInstances: {},
    resourceUtilization: {},
    frameProcessingTime: {},
    processingRates: {},
    serviceHealth: {},
    currentConfig: {},
    serviceLevelCosts: {}
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showHistoricalCosts, setShowHistoricalCosts] = useState(false);
  const [showCostExplanations, setShowCostExplanations] = useState(false);

  // Fetch all monitoring data
  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [
        pendingJobs,
        activeInstances,
        resourceUtilization,
        frameProcessingTime,
        processingRates,
        serviceHealth,
        currentConfig,
        serviceLevelCosts
      ] = await Promise.all([
        monitoringApi.getPendingJobsCount(),
        monitoringApi.getActiveInstances(),
        monitoringApi.getResourceUtilization(),
        monitoringApi.getFrameProcessingTime(),
        monitoringApi.getProcessingRates(),
        monitoringApi.getServiceHealth(),
        monitoringApi.getCurrentConfiguration(),
        monitoringApi.getServiceLevelCosts().catch(err => {
          console.warn('Service-level costs not available:', err);
          return {};
        })
      ]);

      setMetrics({
        pendingJobs,
        activeInstances,
        resourceUtilization,
        frameProcessingTime,
        processingRates,
        serviceHealth,
        currentConfig,
        serviceLevelCosts
      });
      
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch monitoring metrics:', err);
      setError('Failed to load monitoring data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    fetchMetrics();
    
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate summary statistics
  const totalPendingJobs = Object.values(metrics.pendingJobs).reduce((sum, count) => sum + count, 0);
  const totalActiveInstances = Object.values(metrics.activeInstances).reduce((sum, count) => sum + count, 0);
  const avgCpuUsage = Object.values(metrics.resourceUtilization).reduce((sum, service) => sum + (service.cpu || 0), 0) / Math.max(Object.keys(metrics.resourceUtilization).length, 1);
  const totalProcessingRate = Object.values(metrics.processingRates).reduce((sum, rate) => sum + (rate || 0), 0);

  // Service status mapping based on actual health checks
  const getServiceStatus = (serviceName) => {
    const healthStatus = metrics.serviceHealth[serviceName] || { status: 'unknown' };
    const pendingJobs = metrics.pendingJobs[serviceName] || 0;
    
    // Use actual health check status as primary indicator
    if (healthStatus.status === 'up') {
      // Service is healthy, but check if it's degraded due to high load
      if (pendingJobs > 50) return 'degraded';
      return 'healthy';
    } else if (healthStatus.status === 'down') {
      return 'down';
    } else {
      // Unknown status - could be starting up or having issues
      return 'unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'down': return 'error';
      case 'unknown': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleOutlined />;
      case 'degraded': return <ExclamationCircleOutlined />;
      case 'down': return <CloseCircleOutlined />;
      case 'unknown': return <ExclamationCircleOutlined />;
      default: return <ExclamationCircleOutlined />;
    }
  };

  // Priority Metrics Summary Cards
  const renderPriorityMetrics = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Total Pending Messages"
            value={totalPendingJobs}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: totalPendingJobs > 50 ? '#ff4d4f' : totalPendingJobs > 10 ? '#faad14' : '#52c41a' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Active Instances"
            value={totalActiveInstances}
            prefix={<CloudServerOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Avg CPU Usage"
            value={avgCpuUsage.toFixed(1)}
            suffix="%"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: avgCpuUsage > 80 ? '#ff4d4f' : avgCpuUsage > 60 ? '#faad14' : '#52c41a' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Processing Rate"
            value={totalProcessingRate}
            suffix="msg/min"
            prefix={<ThunderboltOutlined />}
            valueStyle={{ color: totalProcessingRate > 0 ? '#52c41a' : '#ff4d4f' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // Service Health Grid (3x2)
  const renderServiceHealthGrid = () => {
    const services = [
      'frame-extractor',
      'solo-analyzer', 
      'facemesh-analyzer',
      'batched-aggregator',
      'web-api',
      'web-client'
    ];

    return (
      <Card title="Service Health Status" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {services.map((serviceName) => {
            const status = getServiceStatus(serviceName);
            const instances = metrics.activeInstances[serviceName] || 0;
            const pendingJobs = metrics.pendingJobs[serviceName] || 0;
            const config = metrics.currentConfig[serviceName] || {};
            
            return (
              <Col xs={24} sm={12} lg={8} key={serviceName}>
                <Card 
                  size="small" 
                  style={{ 
                    borderLeft: `4px solid ${
                      status === 'healthy' ? '#52c41a' : 
                      status === 'degraded' ? '#faad14' : 
                      status === 'down' ? '#ff4d4f' : '#d9d9d9'
                    }`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Title level={5} style={{ margin: 0, textTransform: 'capitalize' }}>
                      {serviceName.replace('-', ' ')}
                    </Title>
                    <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
                      {status}
                    </Tag>
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">Active Instances: </Text>
                    <Text strong>{instances}</Text>
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">Pending Messages: </Text>
                    <Text strong>{pendingJobs}</Text>
                  </div>
                  
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">Processing Rate: </Text>
                    <Text strong style={{ color: (metrics.processingRates[serviceName] || 0) > 0 ? '#52c41a' : '#8c8c8c' }}>
                      {(metrics.processingRates[serviceName] || 0).toLocaleString()} msg/min
                    </Text>
                  </div>
                  
                  {(serviceName === 'solo-analyzer' || serviceName === 'facemesh-analyzer' || serviceName === 'frame-extractor' || serviceName === 'batched-aggregator') && metrics.frameProcessingTime[serviceName] && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">Processing Time: </Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        Avg: <Text strong>{metrics.frameProcessingTime[serviceName].avg || 0}ms</Text> | 
                        Min: <Text strong>{metrics.frameProcessingTime[serviceName].min || 0}ms</Text> | 
                        Max: <Text strong>{metrics.frameProcessingTime[serviceName].max || 0}ms</Text>
                        {metrics.frameProcessingTime[serviceName].samples > 0 && (
                          <Text style={{ marginLeft: 4 }}>({metrics.frameProcessingTime[serviceName].samples} samples)</Text>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {config.cpu && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">CPU: </Text>
                      <Text strong>{config.cpu}</Text>
                    </div>
                  )}
                  
                  {config.memory && (
                    <div style={{ marginBottom: 8 }}>
                      <Text type="secondary">Memory: </Text>
                      <Text strong>{config.memory}</Text>
                    </div>
                  )}
                  
                  <Space style={{ marginTop: 8 }}>
                    <Button size="small" type="link">View Logs</Button>
                    <Button size="small" type="link">View Metrics</Button>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  // Resource Utilization Charts
  const renderResourceUtilization = () => (
    <Card title="Resource Utilization" style={{ marginBottom: 24 }}>
      <Row gutter={[16, 16]}>
        {Object.entries(metrics.resourceUtilization).map(([serviceName, metrics]) => (
          <Col xs={24} sm={12} lg={8} key={serviceName}>
            <Card size="small" title={serviceName.replace('-', ' ')}>
              <div style={{ marginBottom: 16 }}>
                <Text>CPU Usage</Text>
                <Progress 
                  percent={metrics.cpu || 0} 
                  status={metrics.cpu > 80 ? 'exception' : metrics.cpu > 60 ? 'active' : 'normal'}
                  strokeColor={metrics.cpu > 80 ? '#ff4d4f' : metrics.cpu > 60 ? '#faad14' : '#52c41a'}
                />
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <Text>Memory Usage</Text>
                <Progress 
                  percent={metrics.memory || 0} 
                  status={metrics.memory > 85 ? 'exception' : metrics.memory > 70 ? 'active' : 'normal'}
                  strokeColor={metrics.memory > 85 ? '#ff4d4f' : metrics.memory > 70 ? '#faad14' : '#52c41a'}
                />
              </div>
              
              {metrics.network && (
                <div>
                  <Text>Network I/O</Text>
                  <Text strong style={{ display: 'block' }}>
                    {metrics.network.bytesSent} MB sent / {metrics.network.bytesReceived} MB received
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </Card>
  );

  // Configuration Panel
  const renderConfigurationPanel = () => (
    <Card title="Current Configuration" style={{ marginBottom: 24 }}>
      <Row gutter={[16, 16]}>
        {Object.entries(metrics.currentConfig).map(([serviceName, config]) => (
          <Col xs={24} sm={12} lg={8} key={serviceName}>
            <Card size="small" title={serviceName.replace('-', ' ')}>
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">CPU: </Text>
                <Text strong>{config.cpu || 'N/A'}</Text>
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Memory: </Text>
                <Text strong>{config.memory || 'N/A'}</Text>
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Min Instances: </Text>
                <Text strong>{config.minInstances || 'N/A'}</Text>
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Max Instances: </Text>
                <Text strong>{config.maxInstances || 'N/A'}</Text>
              </div>
              
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary">Concurrency: </Text>
                <Text strong>{config.concurrency || 'N/A'}</Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </Card>
  );

  // Service-Level Cost Analysis
  const renderServiceLevelCosts = () => {
    if (!metrics.serviceLevelCosts || Object.keys(metrics.serviceLevelCosts).length === 0) {
      return (
        <Card 
          title={
            <Space>
              <DollarOutlined />
              <span>Service-Level Cost Analysis</span>
              <Tooltip title="Real-time cost tracking per service with resource usage">
                <Button type="text" size="small" icon={<ExclamationCircleOutlined />} />
              </Tooltip>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message="Cost data is being collected"
            description="Pipeline-native cost tracking is active. Data will appear once video processing completes."
            type="info"
            showIcon
          />
        </Card>
      );
    }

    const costsData = metrics.serviceLevelCosts;
    const totalCost = costsData.totalCost || 0;
    const serviceCosts = costsData.serviceCosts || {};

    // Calculate service cost breakdown for pie chart display
    const serviceBreakdown = Object.entries(serviceCosts).map(([serviceName, costData]) => ({
      service: serviceName,
      cost: costData.totalCost || 0,
      percentage: totalCost > 0 ? ((costData.totalCost || 0) / totalCost * 100) : 0,
      frames: costData.frameCount || 0,
      avgTimeMs: costData.avgProcessingTime || 0
    })).sort((a, b) => b.cost - a.cost);

    const costColumns = [
      {
        title: 'Service',
        dataIndex: 'service',
        key: 'service',
        render: (service) => <Text strong>{service}</Text>
      },
      {
        title: 'Total Cost',
        dataIndex: 'cost',
        key: 'cost',
        render: (cost) => <Text>${cost.toFixed(6)}</Text>,
        sorter: (a, b) => a.cost - b.cost,
        defaultSortOrder: 'descend'
      },
      {
        title: 'Cost Share',
        dataIndex: 'percentage',
        key: 'percentage',
        render: (percentage, record) => (
          <div>
            <Progress 
              percent={percentage} 
              size="small" 
              showInfo={false}
              strokeColor={
                record.service === 'frame-extractor' ? '#1890ff' :
                record.service === 'solo-analyzer' ? '#52c41a' :
                record.service === 'facemesh-analyzer' ? '#faad14' :
                record.service === 'batched-aggregator' ? '#722ed1' :
                '#13c2c2'
              }
            />
            <Text type="secondary">{percentage.toFixed(1)}%</Text>
          </div>
        )
      },
      {
        title: 'Frames',
        dataIndex: 'frames',
        key: 'frames',
        render: (frames) => <Text>{frames.toLocaleString()}</Text>
      },
      {
        title: 'Avg Time',
        dataIndex: 'avgTimeMs',
        key: 'avgTimeMs',
        render: (timeMs) => <Text>{timeMs.toFixed(0)}ms</Text>
      }
    ];

    return (
      <Card 
        title={
          <Space>
            <DollarOutlined />
            <span>Service-Level Cost Analysis</span>
            <Tooltip title="Real-time cost tracking per service with resource usage">
              <Button type="text" size="small" icon={<ExclamationCircleOutlined />} />
            </Tooltip>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Total Cost (24h)"
                value={totalCost}
                precision={6}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Cost per Video"
                value={costsData.costPerVideo || 0}
                precision={4}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title="Videos/Hour Rate"
                value={costsData.throughputVideosPerHour || 0}
                precision={1}
                suffix="videos/h"
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        <Divider />

        <div style={{ marginBottom: 16 }}>
          <Title level={4}>
            <PieChartOutlined /> Service Cost Breakdown
          </Title>
        </div>

        <Table
          dataSource={serviceBreakdown}
          columns={costColumns}
          pagination={false}
          size="small"
          rowKey="service"
        />

        {costsData.optimizationRecommendations && costsData.optimizationRecommendations.length > 0 && (
          <>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <Title level={5}>💡 Cost Optimization Recommendations</Title>
              {costsData.optimizationRecommendations.map((rec, index) => (
                <Alert
                  key={index}
                  message={rec.title}
                  description={rec.description}
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                />
              ))}
            </div>
          </>
        )}
      </Card>
    );
  };

  if (loading && !lastUpdated) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading monitoring data...</div>
      </div>
    );
  }

  return (
    <div className="monitoring-dashboard">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>
          <DashboardOutlined /> Pipeline Monitoring Dashboard
        </Title>
        
        <Space>
          {lastUpdated && (
            <Text type="secondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Text>
          )}
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={fetchMetrics}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Priority Metrics Summary */}
      {renderPriorityMetrics()}

      {/* Service Health Grid */}
      {renderServiceHealthGrid()}

      {/* Service-Level Cost Analysis */}
      {Object.keys(metrics.serviceLevelCosts).length > 0 && renderServiceLevelCosts()}

      {/* Resource Utilization */}
      {Object.keys(metrics.resourceUtilization).length > 0 && renderResourceUtilization()}

      {/* Configuration Panel */}
      {Object.keys(metrics.currentConfig).length > 0 && renderConfigurationPanel()}

      {/* Historical Cost Analysis */}
      <Card 
        title="Historical Cost Analysis" 
        size="small" 
        style={{ marginTop: 16 }}
        extra={
          <Button 
            type="link" 
            onClick={() => setShowHistoricalCosts(!showHistoricalCosts)}
          >
            {showHistoricalCosts ? 'Hide' : 'Show'} Historical Data
          </Button>
        }
      >
        {showHistoricalCosts ? (
          <HistoricalCostDashboard />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
            <Text>Click "Show Historical Data" to view long-term cost trends and analysis</Text>
          </div>
        )}
      </Card>

      {/* Cost Explanation Panel */}
      <Card 
        title="Cost Analysis & Explanations" 
        size="small" 
        style={{ marginTop: 16 }}
        extra={
          <Button 
            type="link" 
            icon={<InfoCircleOutlined />}
            onClick={() => setShowCostExplanations(!showCostExplanations)}
          >
            {showCostExplanations ? 'Hide' : 'Show'} Cost Explanations
          </Button>
        }
      >
        {showCostExplanations ? (
          <CostExplanationPanel costData={metrics.serviceLevelCosts} />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
            <Text>Click "Show Cost Explanations" to understand how costs are calculated and optimize spending</Text>
          </div>
        )}
      </Card>

      <Divider />
      
      <div style={{ textAlign: 'center', color: '#8c8c8c' }}>
        <Text>Monitoring data refreshes automatically every 30 seconds</Text>
      </div>
    </div>
  );
};

export default MonitoringDashboard;
